/**
 * Copyright ToolBelt Data Inc., 2020 All Rights Reserved
 */

import React, { useState, useMemo, useEffect } from 'react';

import SectionTogglePanel from "../../../../common/components/SectionTogglePanel";
import TogglePanel from "../../../../common/components/TogglePanel";
import FilterComponentBuilder from "./FilterComponentBuilder";

import { getFilters } from '../utils/FilterUtils';
import { titleCase } from '../utils/titleCase';
import { getGroupIcon } from '../utils/groupIcons';

const MultiSubgroupFilterSection = (props) => {
    const {
        groupKey,      // Ex. 'PROFILE' Or 'SERVICES'
        title,         // Ex. 'PROFILE'
        subTitle,
        subGroups,     // Array ของ Subgroups Ex. [{ key: 'OVERVIEW', label: 'OVERVIEW' }, ...]
        defaultMainOpen = false,
        appearance = 'default',
        filterState,
        handleKeyValueChange,
        metaDataObj,
        handleSubmitFilter,
        handleRemoveFilter,
        searchText,
        productSubscription,
        productKey
    } = props;

    const compact = appearance === 'compact';
    const card = appearance === 'card';
    const subgroupVariant = card ? 'bullet' : (compact ? 'wireframe' : 'default');

    // Initial State
    const [openSections, setOpenSections] = useState(() => {
        const initial = { MAIN: defaultMainOpen };
        subGroups.forEach(sg => { initial[sg.key] = sg.defaultOpen || false; });
        return initial;
    });

    const toggleSection = (key, value) => {
        setOpenSections(prev => ({ ...prev, [key]: value }));
    };

    // Logic
    const allFilteredData = useMemo(() => {
        const dataMap = {};
        subGroups.forEach(sg => {
            const raw = getFilters(groupKey, sg.key, productSubscription, productKey);
            if (!searchText) {
                dataMap[sg.key] = raw;
            } else {
                const lower = searchText.toLowerCase();
                dataMap[sg.key] = raw.filter(opt =>
                    opt.label?.toLowerCase().includes(lower) ||
                    opt.id?.toLowerCase().includes(lower)
                );
            }
        });
        return dataMap;
    }, [groupKey, subGroups, searchText, productSubscription, productKey]);

    // Auto-expand on search transitions only.
    //
    // Deliberately narrowed to [searchText]: `subGroups` is an inline array
    // literal in the parent universe filter components, so its reference
    // changes every render, and `allFilteredData` is a useMemo that depends on
    // `subGroups` so it also changes every render. Including either in the dep
    // list makes this effect fire on every parent re-render — including when
    // the user types into a filter input — which resets `openSections` to
    // defaults mid-keystroke and collapses the subgroup the user just opened.
    // That detaches the input from the DOM and silently drops further input.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (searchText) {
            const newOpenState = { MAIN: true };
            subGroups.forEach(sg => {
                newOpenState[sg.key] = allFilteredData[sg.key]?.length > 0;
            });
            setOpenSections(newOpenState);
        } else {
            const initial = { MAIN: defaultMainOpen };
            subGroups.forEach(sg => { initial[sg.key] = sg.defaultOpen || false; });
            setOpenSections(initial);
        }
    }, [searchText]);

    const hasAnyMatch = Object.values(allFilteredData).some(arr => arr.length > 0);
    if (!hasAnyMatch) return null;

    const innerPanels = subGroups.map(sg => {
        const options = allFilteredData[sg.key];
        if (!options || options.length === 0) return null;

        return (
            <TogglePanel
                key={sg.key}
                title={card ? titleCase(sg.label) : sg.label}
                isExpand={openSections[sg.key]}
                handleOnClick={(val) => toggleSection(sg.key, val)}
                variant={subgroupVariant}
            >
                <FilterComponentBuilder
                    groupKey={groupKey}
                    compact={compact}
                    filterOptions={options}
                    stateObj={filterState}
                    handleKeyValueChange={handleKeyValueChange}
                    metaDataObj={metaDataObj}
                    handleSubmitFilter={handleSubmitFilter}
                    handleRemoveFilter={handleRemoveFilter}
                    productSubscription={productSubscription}
                />
            </TogglePanel>
        );
    });

    return (
        <SectionTogglePanel
            title={card ? titleCase(title) : title}
            subTitle={subTitle}
            appearance={appearance}
            icon={card ? getGroupIcon(groupKey) : undefined}
            isExpand={openSections.MAIN}
            handleOnClick={(val) => toggleSection('MAIN', val)}
        >
            {innerPanels}
        </SectionTogglePanel>
    );
};

export default MultiSubgroupFilterSection;
